import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IContactGrouping, IContactGroupSettingsForm } from 'contacts/types';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { corporateService } from 'corporate/services';

interface IProps {
  isOpen: boolean;
  onClose: (reset: UseFormReset<IContactGroupSettingsForm>) => void;
  onSubmit: (
    reset: UseFormReset<IContactGroupSettingsForm>
  ) => (data: IContactGroupSettingsForm) => void;
}

export const ContactGroupFormFields = ({ isOpen, onClose, onSubmit }: IProps) => {
  const { t } = useTranslation();

  const { watch } = useFormContext();
  const isVendor = watch('isVendor');

  const switchFields = [
    {
      name: 'isCustomer',
      label: t('labelContactGroupIsCustomer'),
    },
    {
      name: 'isVendor',
      label: t('labelContactGroupIsVendor'),
    },
    {
      name: 'automatedPricing',
      label: t('labelContactGroupAutomatedPricing'),
    },
    {
      name: 'autoLeadTimeEnabled',
      label: t('labelContactGroupAutoLeadTimeEnabled'),
    },
    {
      name: 'showInventory',
      label: t('labelContactGroupShowInventory'),
    },
    {
      name: 'freightEnabled',
      label: t('labelContactGroupFreightEnabled'),
    },
  ];

  return (
    <Form isOpen={isOpen} onSubmit={onSubmit} onClose={onClose} title={t('titleEditSettings')}>
      <FormField
        name="contactGrouping"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          label: t('labelContactGrouping'),
          placeholder: t('placeholderSelectContactGrouping'),
          getOptions: corporateService.getContactGroupings,
          getLabel: (option: IContactGrouping) => option.name,
          enabledOnFocus: false,
        }}
      />
      <FormField
        name="externalCustomerNumber"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelCustomerNumber'),
          placeholder: t('placeholderEnterDigits', { count: 6 }),
          slotProps: {
            input: {
              maxLength: 6,
              pattern: '[0-9]{6}',
            },
          },
        }}
      />
      <FormField
        name="accountsReceivableId"
        type={InputType.TEXT_INPUT}
        inputProps={{
          label: t('labelAcctsReceivableId'),
        }}
      />

      <>
        {switchFields.map((field) => (
          <FormField
            key={field.name}
            type={InputType.SWITCH}
            name={field.name}
            inputProps={{ label: field.label }}
          />
        ))}
      </>
      <FormField
        name="domains"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          label: t('labelDomains'),
          options: [],
          getLabel: (option: { title: string }) => option.title,
          hasCreateOption: true,
          multiple: true,
          enabledOnFocus: false,
        }}
      />
      {isVendor && (
        <>
          <FormField
            name="vendorId"
            type={InputType.TEXT_INPUT}
            inputProps={{
              label: t('labelVendorId'),
              placeholder: t('placeholderEnterVendorId'),
            }}
          />
          <FormField
            name="vendorName"
            type={InputType.TEXT_INPUT}
            inputProps={{
              label: t('labelVendorName'),
              placeholder: t('placeholderEnterVendorName'),
            }}
          />
          <FormField
            name="alternateVendorId"
            type={InputType.TEXT_INPUT}
            inputProps={{
              label: t('labelAlternateVendorId'),
              placeholder: t('placeholderEnterAlternateVendorId'),
            }}
          />
          <FormField
            name="alternateVendorName"
            type={InputType.TEXT_INPUT}
            inputProps={{
              label: t('labelAlternateVendorName'),
              placeholder: t('placeholderEnterAlternateVendorName'),
            }}
          />
        </>
      )}
    </Form>
  );
};
